import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import ScorecardTemplateForm from "@/components/acquisitions/ScorecardTemplateForm";

export default function ScorecardTemplates() {
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const queryClient = useQueryClient();

  const { data: templates = [] } = useQuery({
    queryKey: ["scorecard-templates"],
    queryFn: () => base44.entities.ScorecardTemplate.list("-created_date"),
  });

  const createTemplateMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.ScorecardTemplate.create({
        ...data,
        created_by: user.email,
        created_by_name: user.full_name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scorecard-templates"] });
      setShowForm(false);
      setEditingTemplate(null);
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ScorecardTemplate.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scorecard-templates"] });
      setShowForm(false);
      setEditingTemplate(null);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id) => base44.entities.ScorecardTemplate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scorecard-templates"] });
    },
  });

  const handleSubmit = (data) => {
    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Scorecard-maler</h1>
            <p className="text-slate-600 mt-1">Administrer evalueringskriterier for oppkjøpskandidater</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Ny mal
          </Button>
        </div>

        {templates.length === 0 ? (
          <Card className="p-12 text-center bg-white border-0 shadow-sm">
            <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen maler ennå</h3>
            <p className="text-slate-500 mb-6">Opprett din første scorecard-mal</p>
            <Button onClick={() => setShowForm(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" /> Opprett mal
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="p-6 bg-white border-0 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Star className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowForm(true);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Er du sikker på at du vil slette denne malen?")) {
                          deleteTemplateMutation.mutate(template.id);
                        }
                      }}
                      className="text-rose-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">
                    {template.categories.length} kategorier
                  </p>
                  <div className="space-y-1">
                    {template.categories.slice(0, 3).map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{cat.category}</span>
                        <span className="text-slate-500">Vekt: {cat.weight}</span>
                      </div>
                    ))}
                    {template.categories.length > 3 && (
                      <p className="text-xs text-slate-500">
                        +{template.categories.length - 3} flere...
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Opprettet av {template.created_by_name || template.created_by}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <ScorecardTemplateForm
          template={editingTemplate}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingTemplate(null);
          }}
          isLoading={createTemplateMutation.isPending || updateTemplateMutation.isPending}
        />
      )}
    </div>
  );
}